import { useNavigate } from "react-router-dom";
import { formatDate } from "../utils/date";
import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { ROLES } from "../lib/rbac";
import { useHasPermission } from "../contexts/PermissionsContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../components/ui/dialog";
import { Pencil } from "lucide-react";
import { toast } from "sonner";
import RowActions from "../components/RowActions";
import ShowDeletedToggle, { DeletedRowDecorator, DeletedPill } from "../components/ShowDeletedToggle";
import { rowOpenProps } from "../hooks/useRowOpen";

export default function Departments() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role === ROLES.KAAFI_ADMIN;
    const canManageModules = useHasPermission("modules.write");
    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [editName, setEditName] = useState("");
    const [showDeleted, setShowDeleted] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/modules", { params: showDeleted ? { include_deleted: true } : {} });
            setModules(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showDeleted]);

    const toggleActive = async (mod) => {
        try {
            await api.patch(`/modules/${mod.id}`, { active: !mod.active });
            toast.success(
                `${mod.name} ${!mod.active ? "activated" : "deactivated"}`,
            );
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const startEdit = (mod) => {
        setEditing(mod);
        setEditName(mod.name);
    };

    const saveEdit = async () => {
        if (!editing) return;
        try {
            await api.patch(`/modules/${editing.id}`, { name: editName.trim() });
            toast.success("Department renamed");
            setEditing(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <AppShell title="Departments & Worksites">
            <section className="space-y-5" data-testid="departments-page">
                <div className="flex items-center justify-between">
                    <p className="text-sm text-[color:var(--asl-muted)] max-w-2xl">
                        The three operational departments managed under ASL.
                        {canManageModules && (
                            <>
                                {" "}
                                You may rename or deactivate any department.
                            </>
                        )}
                    </p>
                    {canManageModules && (
                        <ShowDeletedToggle value={showDeleted} onChange={setShowDeleted} testid="dept-show-deleted" />
                    )}
                </div>

                {loading ? (
                    <div
                        className="asl-card p-6 text-sm text-[color:var(--asl-muted)]"
                        data-testid="departments-loading"
                    >
                        Loading departments…
                    </div>
                ) : (
                    <div className="asl-card overflow-hidden">
                        <table className="asl-table asl-table--zebra" data-testid="departments-table">
                            <thead
                                className="text-[11px] uppercase tracking-wider text-[color:var(--asl-muted)]"
                                style={{
                                    background: "var(--asl-navy-50)",
                                    borderBottom: "1px solid var(--asl-border)",
                                }}
                            >
                                <tr>
                                    <th className="text-left px-5 py-3">Code</th>
                                    <th className="text-left px-5 py-3">Name</th>
                                    <th className="text-left px-5 py-3">Status</th>
                                    <th className="text-left px-5 py-3">Created</th>
                                    {canManageModules && (
                                        <th className="text-right px-5 py-3">Actions</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {modules.map((m) => (
                                    <tr
                                        key={m.id}
                                        {...rowOpenProps(navigate, () => canManageModules && startEdit(m), { testid: `dept-row-${m.code}`, className: "border-t" })}
                                        style={{ borderColor: "var(--asl-border)" }}
                                    >
                                        <td className="px-5 py-4 font-semibold asl-crimson-text mono">
                                            <DeletedRowDecorator deleted={!!m.deleted_at}>{m.code}</DeletedRowDecorator>
                                        </td>
                                        <td className="px-5 py-4 font-medium asl-navy-text">
                                            <DeletedRowDecorator deleted={!!m.deleted_at}>{m.name}</DeletedRowDecorator>
                                            {m.deleted_at && <DeletedPill deletedAt={m.deleted_at} />}
                                        </td>
                                        <td className="px-5 py-4">
                                            {canManageModules ? (
                                                <div className="flex items-center gap-2">
                                                    <Switch
                                                        checked={m.active}
                                                        onCheckedChange={() =>
                                                            toggleActive(m)
                                                        }
                                                        data-testid={`department-toggle-${m.code}`}
                                                    />
                                                    <span className="text-xs text-[color:var(--asl-muted)]">
                                                        {m.active ? "Active" : "Inactive"}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span
                                                    className={`text-xs px-2 py-1 rounded-full ${
                                                        m.active
                                                            ? "bg-emerald-50 text-emerald-700"
                                                            : "bg-gray-100 text-gray-500"
                                                    }`}
                                                >
                                                    {m.active ? "Active" : "Inactive"}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-5 py-4 text-[color:var(--asl-muted)] text-xs mono">
                                            {formatDate(m.created_at)}
                                        </td>
                                        {canManageModules && (
                                            <td className="px-5 py-4 text-right" data-no-row-click>
                                                <div className="flex items-center justify-end gap-1">
                                                    {!m.deleted_at && (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => startEdit(m)}
                                                            data-testid={`department-edit-${m.code}`}
                                                        >
                                                            <Pencil className="h-3.5 w-3.5 mr-1" />
                                                            Rename
                                                        </Button>
                                                    )}
                                                    <RowActions
                                                        entity="modules"
                                                        endpoint="/modules"
                                                        id={m.id}
                                                        label={m.code}
                                                        deleted={!!m.deleted_at}
                                                        hardDeleteSupported={false}
                                                        onChange={load}
                                                        testid={`dept-actions-${m.code}`}
                                                    />
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent data-testid="department-edit-dialog">
                    <DialogHeader>
                        <DialogTitle>Rename Department</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2 py-2">
                        <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            data-testid="department-edit-name-input"
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setEditing(null)}
                            data-testid="department-edit-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={saveEdit}
                            className="asl-navy-bg text-white"
                            disabled={!editName.trim()}
                            data-testid="department-edit-save"
                        >
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AppShell>
    );
}
